import React, { FC } from 'react';
import styles from './index.module.css';
import GreenTick from './images/ic-green-tick.svg';
import { integrationsInfo } from './integrationsInfo';
import { MemberRole } from '../../../../clients/sidebar/models/CurrentUserProject';
import { IntegrationActions } from './integrationActions';
import { McpIntegrationActions } from './mcpIntegrationActions';
import { IntegrationIcon } from './integrationIcon';
import { Tag, Tooltip } from 'antd';

type Props = {
  premiumIntegrationsAllowed: boolean;
  isTrialUser: boolean;
  integrationList: {
    [key: string]: {
      id?: string;
      enabled: boolean;
      loaded?: boolean | null;
    };
  };
  name: string;
  handleShowIntegration: (name: string) => void;
  handleShowUpgrade: (name: string) => void;
  role?: MemberRole;
  editingAllowed: boolean;
  setPopOverToShow: (integrationName: string) => void;
  popOverToShow: string;
  ownerEmail: string;
  bugherdUrl: string;
  organizationId: string;
  setShowPreview: (showPreview: string) => void;
  index?: number;
  isBeta?: boolean;
};

type IntegrationActionsSlotProps = {
  mobileView?: boolean;
};

// TODO: refactor this, it shouldn't exist. We end up needing to maintain lists and lists of integrations
export const premiumIntegrations = [
  'jira',
  'jiranative',
  'asana',
  'azuredevops',
  'linear',
  'clickup',
  'clickupnative',
  'monday.com',
  'mondaynative',
  'teamwork'
];

export const IntegrationItem: FC<Props> = ({
  name,
  premiumIntegrationsAllowed,
  isTrialUser,
  integrationList,
  handleShowIntegration,
  handleShowUpgrade,
  editingAllowed,
  setPopOverToShow,
  popOverToShow,
  ownerEmail,
  bugherdUrl,
  organizationId,
  setShowPreview,
  index,
  isBeta
}) => {
  const isPremiumIntegration = premiumIntegrations.includes(name);

  const { enabled = false } = integrationList[name] || {};

  const integrationNameParsed = name.startsWith('custom.')
    ? name.charAt(7).toUpperCase() + name.slice(8)
    : name.charAt(0).toUpperCase() + name.slice(1);

  const integrationName = name.replace(/\./g, '');
  const integrationInfo = integrationsInfo[integrationName] || {};
  const {
    link,
    icon,
    description,
    label,
    video,
    articleId,
    kind
  } = integrationInfo;
  const isMcpIntegration = kind === 'mcp';
  const isConnectedButton = enabled || label === 'Webhooks';

  const integrationsActions = ({
    mobileView
  }: IntegrationActionsSlotProps = {}) => {
    if (isMcpIntegration) {
      return (
        <McpIntegrationActions
          mobileView={mobileView}
          articleId={articleId as number}
          clientName={label || integrationNameParsed}
          enabled={enabled}
          buttonDisabled={!editingAllowed}
        />
      );
    }

    return (
      <IntegrationActions
        mobileView={mobileView}
        name={name}
        link={link ?? ''}
        articleId={String(articleId ?? '')}
        video={video ?? false}
        isConnectedButton={isConnectedButton}
        isPremiumIntegration={isPremiumIntegration}
        integrationName={integrationName}
        enabled={enabled}
        buttonDisabled={!editingAllowed}
        onButtonClick={() => {
          if (!isPremiumIntegration || isTrialUser || premiumIntegrationsAllowed) {
            handleShowIntegration(name);
          } else {
            handleShowUpgrade(name);
          }
        }}
        isTrialUser={isTrialUser}
        premiumIntegrationsAllowed={premiumIntegrationsAllowed}
        setPopOverToShow={setPopOverToShow}
        popOverToShow={popOverToShow}
        ownerEmail={ownerEmail}
        bugherdUrl={bugherdUrl}
        organizationId={organizationId}
        setShowPreview={setShowPreview}
      />
    );
  };

  return (
    <li
      id={'integration-' + name}
      className={`${styles.bulletPoint} ${styles.staggerInAnimation}`}
      key={integrationNameParsed}
      style={{ '--index': index }}
    >
      <div className={styles.integrationNameAndIcon}>
        <IntegrationIcon
          name={name}
          icon={icon}
          integrationName={integrationName}
        />
        <div className={styles.integrationNameAndDescription}>
          <div className={styles.labelAndConnected}>
            <IntegrationIcon
              mobileView
              name={name}
              icon={icon}
              integrationName={integrationName}
            />
            <h3>{label}</h3>
            {enabled && label !== 'Webhooks' && (
              <span className={styles.connectedTag}>
                <GreenTick className={styles.connectedTick} />
                Connected
              </span>
            )}
            {(isMcpIntegration || isBeta) && (
              <div className={styles.betaContainer}>
                <Tooltip title="This integration is free to use while in Beta. Try it and let us know what you think.">
                  <Tag style={{ color: '#3F70E0' }} color="#D9E7F4">
                    BETA
                  </Tag>
                </Tooltip>
              </div>
            )}
          </div>
          <p className={styles.integrationDescription}>{description}</p>
          {integrationsActions({ mobileView: true })}
        </div>
      </div>
      {integrationsActions()}
    </li>
  );
};
